import { useTabManager } from './useTabManager';
import { useState, useEffect } from 'react';
import { getIsThisTabActive } from '../utils/tabManager';

/**
 * Utility function to get current app activity status (can be used outside of React)
 * @param {boolean} isOnline - Whether the app is currently online (provided by consumer)
 * @returns {object} status - Current activity status markers.
 */
export const getAppActivity = (isOnline = true) => {
   const isThisTabActive = getIsThisTabActive();
   const isWindowVisible = document.visibilityState === 'visible';
   const isWindowFocused = document.hasFocus();

   return {
      isAppActive: isOnline && isThisTabActive && isWindowVisible && isWindowFocused,
      isOnline,
      isThisTabActive,
      isWindowVisible,
      isWindowFocused,
   };
};

/**
 * Custom hook to track app activity status across multiple dimensions:
 * 1. Online status (Provided by consumer)
 * 2. Multi-tab coordination (useTabManager)
 * 3. Browser visibility (document.visibilityState)
 * 4. Window focus (document.hasFocus)
 *
 * @param {boolean} isOnline - Whether the app is currently online
 * @returns {object} status - Reactive activity status markers.
 */
export const useAppActivity = (isOnline = true) => {
   const { isThisTabActive } = useTabManager();

   const [isWindowVisible, setIsWindowVisible] = useState(() => document.visibilityState === 'visible');
   const [isWindowFocused, setIsWindowFocused] = useState(() => document.hasFocus());

   useEffect(() => {
      const handleVisibilityChange = () => setIsWindowVisible(document.visibilityState === 'visible');
      const handleFocus = () => setIsWindowFocused(true);
      const handleBlur = () => setIsWindowFocused(false);

      window.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);
      window.addEventListener('blur', handleBlur);

      return () => {
         window.removeEventListener('visibilitychange', handleVisibilityChange);
         window.removeEventListener('focus', handleFocus);
         window.removeEventListener('blur', handleBlur);
      };
   }, []);

   const isAppActive = isOnline && isThisTabActive && isWindowVisible && isWindowFocused;

   return {
      isAppActive,
      isOnline,
      isThisTabActive,
      isWindowVisible,
      isWindowFocused,
   };
};
